import * as i0 from '@angular/core';
import { Injectable, Inject, NgModule } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import * as i2 from '@angular/common/http';
import * as i1 from '@upr-web-primer/locale';
import { get, find, pickBy, identity } from 'lodash-es';
import moment from 'moment';

class VenueRequest {
}
class ShowtimeRequest {
}
class MinionService {
    constructor(document, appDefaults, localeService, http) {
        this.document = document;
        this.appDefaults = appDefaults;
        this.localeService = localeService;
        this.http = http;
        this.parks = {
            ioa: 10000,
            usf: 10010,
            citywalk: 10011,
            vb: 13801,
        };
        this.momentFormat = 'MM/DD/YYYY h:mm a';
        if (!this.appDefaults?.VENUES_API_ENDPOINT) {
            console.warn('Minion service will silently fail for calls that require the venues endpoint.');
        }
        if (!this.appDefaults?.SHOWTIMES_API_ENDPOINT) {
            console.warn('Minion service will silently fail for calls that require the showtimes endpoint.');
        }
        const locale = this.localeService.getFromStorage();
        this.language = this.document.documentElement.lang || locale.language;
        moment.updateLocale(this.language, {
            week: { dow: 0, doy: 0 },
        });
        this.venues = new Array();
        this.showtimes = new Array();
    }
    getTimeAndMeridiemByTime(time, format, hideMeridiem) {
        const timeMoment = moment(time, format);
        const timeHours = timeMoment.format('h');
        let timeMinutes = `:${timeMoment.format('mm')}`;
        let formattedTime = '';
        const timeMeridiem = `<span class="meridiem">${timeMoment.format('a')}</span>`;
        if (timeMinutes == ':00') {
            timeMinutes = '';
        }
        if (hideMeridiem) {
            formattedTime = `${timeHours}${timeMinutes}`;
        }
        else {
            formattedTime = `${timeHours}${timeMinutes} ${timeMeridiem}`;
        }
        return formattedTime;
    }
    getObjectTimeAndMeridiemByTime(time, format) {
        const timeMoment = moment(time, format);
        return {
            hour: timeMoment.format('h:mm '),
            meridiem: timeMoment.format('A'),
            datetime: timeMoment.toISOString(),
        };
    }
    getStatusByVenueObj(data) {
        return get(data, 'Hours.0.VenueStatus') || get(data, 'VenueStatus');
    }
    getOpenTime(data, format) {
        const openTimeString = get(data, 'Hours.0.OpenTimeString') || get(data, 'OpenTimeString');
        return format
            ? moment.parseZone(openTimeString).format(format)
            : moment.parseZone(openTimeString).format(this.momentFormat);
    }
    getCloseTime(data, format) {
        const closeTimeString = get(data, 'Hours.0.CloseTimeString') || get(data, 'CloseTimeString');
        return format
            ? moment.parseZone(closeTimeString).format(format)
            : moment.parseZone(closeTimeString).format(this.momentFormat);
    }
    requestVenues(venue, enddate) {
        if (!this.appDefaults?.VENUES_API_ENDPOINT) {
            return Promise.resolve({});
        }
        let venueRequest = find(this.venues, (v) => v.venue === venue && v.enddate === enddate);
        if (!venueRequest) {
            const params = pickBy({ venue, enddate }, identity);
            venueRequest = new VenueRequest();
            venueRequest.venue = venue;
            venueRequest.enddate = enddate;
            venueRequest.data = this.http
                .get(this.appDefaults.VENUES_API_ENDPOINT, { params })
                .toPromise();
            this.venues.push(venueRequest);
        }
        return Promise.resolve(venueRequest.data);
    }
    getVenueDataById(venueID, minonData) {
        return minonData.Results.find((parkObj) => parkObj.Id === venueID);
    }
    getVenueDataByKey(venueName, minionData) {
        const venueID = this.parks[venueName];
        return this.getVenueDataById(venueID, minionData);
    }
    getTodayHoursByVenueObj(venueObj, format) {
        const isHasStatus = this.getStatusByVenueObj(venueObj);
        if (isHasStatus) {
            return isHasStatus;
        }
        const close = this.getTimeAndMeridiemByTime(this.getCloseTime(venueObj), format, false);
        const hideMeridiem = moment(this.getOpenTime(venueObj)).format('a') ===
            moment(this.getCloseTime(venueObj)).format('a') &&
            moment(this.getOpenTime(venueObj)).isBefore(this.getCloseTime(venueObj));
        const open = this.getTimeAndMeridiemByTime(this.getOpenTime(venueObj), format, hideMeridiem);
        return `${open}-${close}`;
    }
    getObjectTodayHoursByVenueObj(venueObj, format) {
        const open = this.getOpenTime(venueObj);
        const close = this.getCloseTime(venueObj);
        return {
            open: this.getObjectTimeAndMeridiemByTime(open, format),
            close: this.getObjectTimeAndMeridiemByTime(close, format),
        };
    }
    requestTridionShowTimes(marketingcode, productid, type = 'month', limit = 3) {
        if (!this.appDefaults?.SHOWTIMES_API_ENDPOINT) {
            return Promise.resolve({});
        }
        let showtimeRequest = find(this.showtimes, (v) => v.marketingcode === marketingcode &&
            v.productid === productid &&
            v.type === type &&
            v.limit === limit);
        if (!showtimeRequest) {
            const params = pickBy({ marketingcode, productid, type, limit }, identity);
            showtimeRequest = new ShowtimeRequest();
            showtimeRequest.marketingcode = marketingcode;
            showtimeRequest.productid = productid;
            showtimeRequest.type = type;
            showtimeRequest.limit = limit;
            showtimeRequest.data = this.http
                .get(this.appDefaults.SHOWTIMES_API_ENDPOINT, { params })
                .toPromise();
            this.showtimes.push(showtimeRequest);
        }
        return Promise.resolve(showtimeRequest.data);
    }
}
MinionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionService, deps: [{ token: DOCUMENT }, { token: 'AppDefaults' }, { token: i1.LocaleService }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
MinionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }, { type: i1.LocaleService }, { type: i2.HttpClient }]; } });

class MinionModule {
}
MinionModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
MinionModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionModule });
MinionModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionModule, providers: [MinionService] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.3.11", ngImport: i0, type: MinionModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [MinionService]
                }]
        }] });

export { MinionModule, MinionService };
